﻿import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Banknote,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  Mail,
  Pencil,
  Phone,
  Plus,
  ShieldCheck,
  Upload,
  UserCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS, type RoleCode } from '@pms/shared';
import { getErrorMessage } from '@/lib/errors';
import { FinancialDataModal } from '@/components/payroll/FinancialDataModal';
import { usersService, type StaffListItem } from '@/services/users.service';
import { rolesService } from '@/services/roles.service';
import { departmentsService } from '@/services/departments.service';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function StaffListPage() {
  const navigate = useNavigate();
  const perms = usePermission();

  // Active / Inactive tab lives in the URL so refresh and back keep it
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: 'active' | 'inactive' = searchParams.get('status') === 'inactive' ? 'inactive' : 'active';

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    q: '',
    department_id: '',
    role: '' as RoleCode | '',
  });

  const baseFilters = {
    q: filters.q || undefined,
    role: filters.role || undefined,
    department_id: filters.department_id || undefined,
  };

  const queryParams = {
    ...baseFilters,
    page,
    limit: 20,
    is_active: tab === 'active',
  };

  const changeTab = (key: string) => {
    setSearchParams(key === 'inactive' ? { status: 'inactive' } : {}, { replace: true });
    setPage(1);
  };

  // Export — sends the same filters as the list; backend returns every match
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await usersService.exportXlsx(queryParams);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  // Leave export — same filters; live credit/balance per staff × leave type
  const [exportingLeave, setExportingLeave] = useState(false);

  const handleLeaveExport = async () => {
    setExportingLeave(true);
    try {
      await usersService.exportLeaveBalancesXlsx(queryParams);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExportingLeave(false);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['staff', queryParams],
    queryFn: () => usersService.list(queryParams),
  });

  // Tab counts — one row each, only pagination.total is read
  const { data: activeCount } = useQuery({
    queryKey: ['staff', 'count', 'active', baseFilters],
    queryFn: () => usersService.list({ ...baseFilters, page: 1, limit: 1, is_active: true }),
  });
  const { data: inactiveCount } = useQuery({
    queryKey: ['staff', 'count', 'inactive', baseFilters],
    queryFn: () => usersService.list({ ...baseFilters, page: 1, limit: 1, is_active: false }),
  });

  const tabLabel = (label: string, total: number | undefined) =>
    total === undefined ? label : `${label} (${total})`;

  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: () => rolesService.list() });
  const { data: depts } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const updateFilter =<K extends keyof typeof filters>(k: K, v: (typeof filters)[K]) => {
    setFilters((prev) => ({ ...prev, [k]: v }));
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground">Manage user accounts and permissions</p>
        </div>
        <div className="flex gap-2">
          {perms.has(PERMISSIONS.STAFF_EXPORT) && (
            <Button
              variant="outline"
              onClick={() => void handleExport()}
              disabled={exporting}
              title={`Exports every ${tab} staff row matching the current filters`}
            >
              <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'Export'}
            </Button>
          )}
          {perms.has(PERMISSIONS.STAFF_EXPORT) && (
            <Button
              variant="outline"
              onClick={() => void handleLeaveExport()}
              disabled={exportingLeave}
              title="Live leave credit & balance of every staff matching the current filters"
            >
              <CalendarDays className="h-4 w-4" /> {exportingLeave ? 'Exporting…' : 'Leave Export'}
            </Button>
          )}
          <Button variant="outline" disabled title="Coming soon">
            <Upload className="h-4 w-4" /> Bulk Upload
          </Button>
          {perms.has(PERMISSIONS.STAFF_CREATE) && (
            <Button onClick={() => navigate('/hrms/staff/new')}>
              <Plus className="h-4 w-4" /> Add User
            </Button>
          )}
        </div>
      </div>

      <Tabs
        className="w-fit"
        active={tab}
        onChange={changeTab}
        tabs={[
          { key: 'active', label: tabLabel('Active', activeCount?.pagination.total) },
          { key: 'inactive', label: tabLabel('Inactive', inactiveCount?.pagination.total) },
        ]}
      />

      {/* Filters bar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <SearchInput
          value={filters.q}
          onChange={(v) => updateFilter('q', v)}
          placeholder="Search by name or email…"
          containerClassName="w-full sm:min-w-[200px] sm:flex-1 sm:max-w-xs"
        />

        <Select
          value={filters.department_id || '__all__'}
          onValueChange={(v) => updateFilter('department_id', v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Departments</SelectItem>
            {(depts?.data ?? []).map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.role || '__all__'}
          onValueChange={(v) => updateFilter('role', v === '__all__' ? '' : v as RoleCode | '')}
        >
          <SelectTrigger className="h-9 w-full sm:w-32">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Roles</SelectItem>
            {(roles ?? []).map((r) => (
              <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {data && (
          <span className="ml-auto text-xs text-muted-foreground">
            {data.pagination.total} {data.pagination.total === 1 ? 'user' : 'users'}
          </span>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-border/60 bg-card px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-40 rounded bg-muted animate-pulse" />
                  <div className="h-2.5 w-64 rounded bg-muted animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (data?.data ?? []).length === 0 ? (
        <div className="rounded-lg border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
          No {tab} staff match the current filters
        </div>
      ) : (
        <div className="space-y-2">
          {(data?.data ?? []).map((u) => (
            <StaffCard
              key={u.id}
              user={u}
              perms={perms}
              onView={() => navigate(`/hrms/staff/${u.id}`)}
              onAccess={() => navigate(`/hrms/staff/${u.id}?tab=access`)}
              onEdit={() => navigate(`/hrms/staff/${u.id}/edit`)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border/60 pt-3 text-sm">
          <span className="text-muted-foreground">
            Page {data.pagination.page} of {data.pagination.totalPages} · {data.pagination.total} total
          </span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StaffCard({
  user,
  perms,
  onView,
  onAccess,
  onEdit,
}: {
  user: StaffListItem;
  perms: ReturnType<typeof usePermission>;
  onView: () => void;
  onAccess: () => void;
  onEdit: () => void;
}) {
  const [financeOpen, setFinanceOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const fullName = `${user.first_name} ${user.last_name}`;
  const photoUrl = user.new_photos_url ?? user.photo_url ?? user.avatar_url;

  return (
    <div className="rounded-lg border border-border/60 bg-card px-4 py-4 transition hover:border-border/80 hover:shadow-md">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">

        {/* Avatar + content */}
        <div className="flex min-w-0 flex-1 items-start gap-3.5">
          {/* Avatar */}
          {photoUrl ? (
            <button
              type="button"
              onClick={() => setPhotoOpen(true)}
              aria-label={`View photo of ${fullName}`}
              className="flex h-12 w-12 shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-full border-2 border-primary/20 bg-primary/5 shadow-sm transition hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <img src={photoUrl} alt={fullName} className="h-full w-full object-cover" />
            </button>
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-primary/20 bg-primary/5 shadow-sm">
              <UserCircle2 className="h-7 w-7 text-primary/40" />
            </div>
          )}

          {/* Text content */}
          <div className="min-w-0 flex-1 space-y-1.5">
            {/* Name row */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <Link
                to={`/hrms/staff/${user.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold uppercase tracking-wide text-primary hover:underline"
              >
                {user.first_name} {user.last_name}
              </Link>
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                {user.employee_code ?? '—'}
              </span>
              <span
                className={
                  user.is_active
                    ? 'inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700'
                    : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground'
                }
              >
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Contact + dept + designation — two sub-rows */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{user.email}</span>
              </span>
              {user.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3 shrink-0" />
                  {user.phone}
                </span>
              )}
            </div>

            {(user.department_name || user.designation_name) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {user.department_name && (
                  <span className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                    <Building2 className="h-3 w-3" /> {user.department_name}
                  </span>
                )}
                {user.designation_name && (
                  <span className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                    <Briefcase className="h-3 w-3" /> {user.designation_name}
                  </span>
                )}
              </div>
            )}

            {/* Roles row */}
            {user.roles.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {user.roles.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-0.5 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    <UserCircle2 className="h-2.5 w-2.5" /> {r}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons — all with icon + label */}
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:ml-auto">
          <Button size="sm" variant="outline" onClick={onView} className="h-8 gap-1.5 px-3">
            <Eye className="h-3.5 w-3.5" /> View
          </Button>
          {perms.has(PERMISSIONS.MODULES_ASSIGN) && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAccess}
              className="h-8 gap-1.5 px-3"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Access
            </Button>
          )}
          {perms.has(PERMISSIONS.STAFF_EDIT) && (
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
              className="h-8 gap-1.5 px-3"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
          {perms.has(PERMISSIONS.SALARY_MANAGE) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFinanceOpen(true)}
              className="h-8 gap-1.5 px-3"
            >
              <Banknote className="h-3.5 w-3.5" /> Finance
            </Button>
          )}
        </div>
      </div>

      {financeOpen && (
        <FinancialDataModal
          userId={user.id}
          employeeName={fullName}
          open={financeOpen}
          onClose={() => setFinanceOpen(false)}
        />
      )}

      {photoOpen && photoUrl && (
        <StaffPhotoDialog
          user={user}
          fullName={fullName}
          photoUrl={photoUrl}
          onViewProfile={onView}
          onClose={() => setPhotoOpen(false)}
        />
      )}
    </div>
  );
}

/** The card avatar at full size; portrait or landscape, never cropped. */
function StaffPhotoDialog({
  user,
  fullName,
  photoUrl,
  onViewProfile,
  onClose,
}: {
  user: StaffListItem;
  fullName: string;
  photoUrl: string;
  onViewProfile: () => void;
  onClose: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const details = [user.employee_code, user.designation_name, user.department_name].filter(Boolean).join(' · ');

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <div className="flex min-h-[240px] items-center justify-center overflow-hidden rounded-lg bg-black/80">
          {failed ? (
            <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
              <UserCircle2 className="h-16 w-16 text-primary/40" />
              Photo could not be loaded
            </div>
          ) : (
            <img
              src={photoUrl}
              alt={fullName}
              onError={() => setFailed(true)}
              className="max-h-[70vh] w-full object-contain"
            />
          )}
        </div>
        <div className="min-w-0 space-y-1">
          <DialogTitle className="truncate text-base font-bold uppercase tracking-wide">{fullName}</DialogTitle>
          {details && <DialogDescription>{details}</DialogDescription>}
        </div>
        <DialogFooter>
          {!failed && (
            <Button variant="outline" asChild>
              <a href={photoUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" /> Open full size
              </a>
            </Button>
          )}
          <Button onClick={onViewProfile}>
            <Eye className="h-4 w-4" /> View profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
